import React from 'react';
import PropTypes from 'prop-types';

const MetricasProceso = ({ procesos = [] }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDuration = (hours) => {
    if (typeof hours !== 'number' || hours < 0) return 'N/A';
    
    if (hours === 0) {
      return '0 min';
    } else if (hours < 1) {
      // Menos de 1 hora: mostrar en minutos
      const minutes = Math.round(hours * 60);
      return `${minutes} min`;
    } else if (hours < 24) {
      // Menos de 24 horas: mostrar horas con minutos
      const totalMinutes = Math.round(hours * 60);
      const hoursPart = Math.floor(totalMinutes / 60);
      const minutesPart = totalMinutes % 60;
      
      if (minutesPart === 0) {
        return hoursPart === 1 ? '1 hora' : `${hoursPart} horas`;
      } else {
        return `${hoursPart}h ${minutesPart}min`;
      }
    } else {
      // 24 horas o más: mostrar días, horas y minutos
      const totalMinutes = Math.round(hours * 60);
      const days = Math.floor(totalMinutes / (24 * 60));
      const remainingMinutes = totalMinutes % (24 * 60);
      const hoursPart = Math.floor(remainingMinutes / 60);
      const minutesPart = remainingMinutes % 60;
      
      let result = '';
      if (days > 0) {
        result += days === 1 ? '1 día' : `${days} días`;
      }
      if (hoursPart > 0) {
        if (result) result += ', ';
        result += hoursPart === 1 ? '1 hora' : `${hoursPart} horas`;
      }
      if (minutesPart > 0) {
        if (result) result += ', ';
        result += `${minutesPart} min`;
      }
      
      return result || '0 min';
    }
  };

  const formatPercentage = (percentage) => {
    if (typeof percentage !== 'number' || percentage < 0) return 'N/A';
    return `${Math.round(percentage)}%`;
  };

  const getResponsable = (proceso) => {
    if (proceso.operario) {
      return proceso.operario;
    } else if (proceso.proveedor) {
      return proceso.proveedor;
    }
    return 'Sin asignar';
  };

  const getEstadoBadgeClass = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'completado':
      case 'finalizado':
        return 'bg-success';
      case 'en proceso':
      case 'en_proceso':
        return 'bg-warning';
      case 'pendiente':
        return 'bg-secondary';
      case 'error':
      case 'fallido':
        return 'bg-danger';
      default:
        return 'bg-info';
    }
  };

  if (!procesos || procesos.length === 0) {
    return (
      <div className="text-center py-4" data-testid="metricas-proceso">
        <i className="fas fa-info-circle fa-2x text-muted mb-3" />
        <p className="text-muted">No hay procesos registrados</p>
      </div>
    );
  }

  return (
    <div data-testid="metricas-proceso">
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th scope="col">Proceso</th>
              <th scope="col">Centro de Costo</th>
              <th scope="col">Responsable</th>
              <th scope="col">Inicio</th>
              <th scope="col">Fin</th>
              <th scope="col">Duración</th>
              <th scope="col">Porcentaje</th>
              <th scope="col">Estado</th>
            </tr>
          </thead>
          <tbody>
            {procesos.map((proceso, index) => (
              <tr key={proceso.id || index}>
                <td>
                  <strong>{proceso.nombre || 'N/A'}</strong>
                </td>
                <td>{proceso.centro_costo || 'N/A'}</td>
                <td>{getResponsable(proceso)}</td>
                <td>
                  <small className="text-muted">
                    {formatDate(proceso.fecha_inicio)}
                  </small>
                </td>
                <td>
                  <small className="text-muted">
                    {formatDate(proceso.fecha_fin)}
                  </small>
                </td>
                <td>
                  <span className="badge bg-primary">
                    {formatDuration(proceso.tiempo_proceso)}
                  </span>
                </td>
                <td>
                  <span className="badge bg-info">
                    {formatPercentage(proceso.porcentaje_tiempo)}
                  </span>
                </td>
                <td>
                  <span className={`badge ${getEstadoBadgeClass(proceso.estado)}`}>
                    {proceso.estado || 'N/A'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-3">
        <small className="text-muted">
          <i className="fas fa-list me-1" />
          Total de procesos: {procesos.length}
        </small>
      </div>
    </div>
  );
};

MetricasProceso.propTypes = {
  /** Array de procesos con sus métricas */
  procesos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    nombre: PropTypes.string,
    centro_costo: PropTypes.string,
    operario: PropTypes.string,
    proveedor: PropTypes.string,
    fecha_inicio: PropTypes.string,
    fecha_fin: PropTypes.string,
    tiempo_proceso: PropTypes.number,
    porcentaje_tiempo: PropTypes.number,
    estado: PropTypes.string
  }))
};

MetricasProceso.defaultProps = {
  procesos: []
};

export default MetricasProceso;
